import React, { useState, useEffect } from 'react'
import { Box, Grid, Paper, Typography, Slider, Fade, TextField } from '@mui/material'
import PageTitle from '../../components/PageTitle/PageTitle'
import InputTextField from '../../components/InputTextField/InputTextField'
import Card from '../../components/MetricCards/Card'

interface CalculatorInputs {
	numberOfLots: number
	averageLotRent: number
	expenseRatio: number
	downPaymentPercentage: number
	interestRate: number
	loanTermYears: number
	offerPrice: number
	mortgagePayment: number | null // null means calculate automatically
}

interface CalculatorResults {
	monthlyIncome: number
	annualIncome: number
	annualExpenses: number
	noi: number
	capRate: number
	downPayment: number
	loanAmount: number
	monthlyMortgagePayment: number
	monthlyCashFlow: number
	annualCashFlow: number
	roi: number
}

const capRateMarks = [
	{ value: 0.05, label: '5%' },
	{ value: 0.08, label: '8%' },
	{ value: 0.12, label: '12%' },
]
const interestRateMarks = [
	{ value: 0.03, label: '3%' },
	{ value: 0.07, label: '7%' },
	{ value: 0.12, label: '12%' },
]
const expenseRatioMarks = [
	{ value: 0.2, label: '20%' },
	{ value: 0.35, label: '35%' },
	{ value: 0.5, label: '50%' },
]
const downPaymentMarks = [
	{ value: 0.1, label: '10%' },
	{ value: 0.5, label: '50%' },
	{ value: 0.9, label: '90%' },
]
const offerPriceMarks = [
	{ value: 100000, label: '$100K' },
	{ value: 690000, label: '$690K' },
	{ value: 2000000, label: '$2M' },
]

const minOfferPrice = 100000
const maxOfferPrice = 2000000
const stepOfferPrice = 1000

const formatCurrency = (value: number): string =>
	`$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const formatPercentage = (value: number): string => `${(value * 100).toFixed(2)}%`

const VerticalMetricBar: React.FC<{ value: number; label: string; maxValue: number }> = ({
	value,
	label,
	maxValue,
}) => {
	const getColor = (value: number) => {
		if (value >= 0.12) return '#52AB1B' // Green for high values
		if (value >= 0.08) return '#F5EED2' // Yellow for medium values
		return '#fee2e2' // Red for low values
	}

	const percentage = Math.min((value / maxValue) * 100, 100)
	const color = getColor(value)

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
			<Typography variant="h6" fontWeight={600}>
				{label}
			</Typography>
			<Box
				sx={{
					height: '200px',
					width: '60px',
					bgcolor: 'grey.100',
					borderRadius: 1,
					overflow: 'hidden',
					position: 'relative',
				}}
			>
				<Box
					sx={{
						position: 'absolute',
						bottom: 0,
						width: '100%',
						height: `${percentage}%`,
						bgcolor: color,
						transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
					}}
				/>
			</Box>
			<Typography variant="h6" fontWeight={600}>
				{formatPercentage(value)}
			</Typography>
		</Box>
	)
}

const numberOfLotsMarks = [
	{ value: 5, label: '5' },
	{ value: 25, label: '25' },
	{ value: 50, label: '50' },
]

const averageLotRentMarks = [
	{ value: 500, label: '$500' },
	{ value: 1000, label: '$1K' },
	{ value: 2000, label: '$2K' },
]

const loanTermMarks = [
	{ value: 5, label: '5y' },
	{ value: 20, label: '20y' },
	{ value: 40, label: '40y' },
]

interface SliderWithInputProps {
	value: number
	min: number
	max: number
	step: number
	marks: { value: number; label: string }[]
	onChange: (value: number) => void
	label: string
	formatValue?: (value: number) => string
}

const SliderWithInput: React.FC<SliderWithInputProps> = ({
	value,
	min,
	max,
	step,
	marks,
	onChange,
	label,
	formatValue = (v) => v.toString(),
}) => {
	const [inputValue, setInputValue] = useState(formatValue(value))
	const [isEditing, setIsEditing] = useState(false)

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value
		setInputValue(newValue)
	}

	const handleInputBlur = () => {
		const numericValue = parseFloat(inputValue.replace(/[^0-9.-]+/g, ''))
		if (!isNaN(numericValue)) {
			const clampedValue = Math.min(Math.max(numericValue, min), max)
			onChange(clampedValue)
			setInputValue(formatValue(clampedValue))
		} else {
			setInputValue(formatValue(value))
		}
		setIsEditing(false)
	}

	const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'Enter') {
			handleInputBlur()
		}
	}

	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
				<Typography variant="h6">
					{label}:{' '}
					{isEditing ? (
						<TextField
							value={inputValue}
							onChange={handleInputChange}
							onBlur={handleInputBlur}
							onKeyPress={handleKeyPress}
							size="small"
							autoFocus
							sx={{ width: '100px' }}
							inputProps={{
								step,
								min,
								max,
								type: 'number',
							}}
						/>
					) : (
						<Box
							component="span"
							onClick={() => setIsEditing(true)}
							sx={{
								cursor: 'pointer',
								'&:hover': {
									textDecoration: 'underline',
								},
							}}
						>
							<b>{formatValue(value)}</b>
						</Box>
					)}
				</Typography>
			</Box>
			<Slider
				value={value}
				min={min}
				max={max}
				step={step}
				marks={marks}
				onChange={(_, newValue) => onChange(newValue as number)}
				valueLabelDisplay="auto"
				sx={{ color: '#6366f1' }}
			/>
		</Box>
	)
}

const MobileHomeParkCalculator: React.FC = () => {
	const [inputs, setInputs] = useState<CalculatorInputs>({
		numberOfLots: 7,
		averageLotRent: 1000,
		expenseRatio: 0.35,
		downPaymentPercentage: 0.5,
		interestRate: 0.07,
		loanTermYears: 20,
		offerPrice: 690000,
		mortgagePayment: null, // null means calculate from interest rate and loan term
	})
	const [results, setResults] = useState<CalculatorResults | null>(null)
	const [error, setError] = useState<string>('')

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target
		setInputs({ ...inputs, [name]: parseFloat(value), mortgagePayment: null }) // Reset mortgage payment when other inputs change
		setError('')
	}

	const handleSliderChange = (name: string) => (_: Event, value: number | number[]) => {
		setInputs({
			...inputs,
			[name]: value as number,
			mortgagePayment: name === 'mortgagePayment' ? (value as number) : null,
		})
		setError('')
	}

	const calculateMortgagePayment = (loanAmount: number, interestRate: number, loanTermYears: number): number => {
		const monthlyInterestRate = interestRate / 12
		const numberOfPayments = loanTermYears * 12
		if (monthlyInterestRate > 0) {
			return (
				(loanAmount * (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments))) /
				(Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1)
			)
		}
		return loanAmount / numberOfPayments
	}

	const calculateLoanAmount = (mortgagePayment: number, interestRate: number, loanTermYears: number): number => {
		const monthlyInterestRate = interestRate / 12
		const numberOfPayments = loanTermYears * 12
		if (monthlyInterestRate > 0) {
			return (
				mortgagePayment *
				((Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1) /
					(monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)))
			)
		}
		return mortgagePayment * numberOfPayments
	}

	const calculate = () => {
		try {
			const {
				numberOfLots,
				averageLotRent,
				expenseRatio,
				downPaymentPercentage,
				interestRate,
				loanTermYears,
				offerPrice,
				mortgagePayment,
			} = inputs
			if (numberOfLots <= 0) throw new Error('Number of lots must be positive.')
			if (averageLotRent <= 0) throw new Error('Average lot rent must be positive.')
			if (expenseRatio < 0 || expenseRatio > 1) throw new Error('Expense ratio must be between 0 and 1.')
			if (downPaymentPercentage < 0 || downPaymentPercentage > 1) {
				throw new Error('Down payment percentage must be between 0 and 1.')
			}
			if (interestRate < 0) throw new Error('Interest rate cannot be negative.')
			if (loanTermYears <= 0) throw new Error('Loan term must be positive.')
			if (offerPrice < 0) throw new Error('Offer price must be non-negative.')
			if (mortgagePayment !== null && mortgagePayment < 0) throw new Error('Mortgage payment cannot be negative.')

			const monthlyIncome = numberOfLots * averageLotRent
			const annualIncome = monthlyIncome * 12
			const annualExpenses = expenseRatio * annualIncome
			const noi = annualIncome - annualExpenses
			const capRate = noi / offerPrice
			const downPayment = downPaymentPercentage * offerPrice

			let loanAmount: number
			let monthlyMortgagePayment: number

			if (mortgagePayment !== null) {
				// Use provided mortgage payment
				monthlyMortgagePayment = mortgagePayment
				loanAmount = calculateLoanAmount(mortgagePayment, interestRate, loanTermYears)
			} else {
				// Calculate mortgage payment from loan amount
				loanAmount = offerPrice - downPayment
				monthlyMortgagePayment = calculateMortgagePayment(loanAmount, interestRate, loanTermYears)
			}

			const monthlyExpenses = annualExpenses / 12
			const monthlyCashFlow = monthlyIncome - monthlyExpenses - monthlyMortgagePayment
			const annualCashFlow = monthlyCashFlow * 12
			const roi = downPayment > 0 ? annualCashFlow / downPayment : 0

			setResults({
				monthlyIncome,
				annualIncome,
				annualExpenses,
				noi,
				capRate,
				downPayment,
				loanAmount,
				monthlyMortgagePayment,
				monthlyCashFlow,
				annualCashFlow,
				roi,
			})
			setError('')
		} catch (err) {
			setError(err instanceof Error ? err.message : 'An error occurred')
			setResults(null)
		}
	}

	useEffect(() => {
		calculate()
	}, [inputs])

	const metrics = results
		? [
				{ key: 'monthlyIncome', title: 'Monthly Income', value: formatCurrency(results.monthlyIncome) },
				{ key: 'annualIncome', title: 'Annual Income', value: formatCurrency(results.annualIncome) },
				{ key: 'annualExpenses', title: 'Annual Expenses', value: formatCurrency(results.annualExpenses) },
				{ key: 'noi', title: 'NOI', value: formatCurrency(results.noi) },
				{ key: 'downPayment', title: 'Down Payment', value: formatCurrency(results.downPayment) },
				{ key: 'loanAmount', title: 'Loan Amount', value: formatCurrency(results.loanAmount) },
				{
					key: 'monthlyMortgagePayment',
					title: 'Monthly Mortgage Payment',
					value: formatCurrency(results.monthlyMortgagePayment),
				},
				{ key: 'monthlyCashFlow', title: 'Monthly Cash Flow', value: formatCurrency(results.monthlyCashFlow) },
				{ key: 'annualCashFlow', title: 'Annual Cash Flow', value: formatCurrency(results.annualCashFlow) },
			]
		: []

	// Calculate max mortgage payment for slider (based on max offer price)
	const maxMortgagePayment = calculateMortgagePayment(
		maxOfferPrice * (1 - inputs.downPaymentPercentage),
		inputs.interestRate,
		inputs.loanTermYears,
	)
	const mortgagePaymentMarks = [
		{ value: 0, label: '$0' },
		{ value: maxMortgagePayment / 2, label: formatCurrency(maxMortgagePayment / 2) },
		{ value: maxMortgagePayment, label: formatCurrency(maxMortgagePayment) },
	]

	return (
		<Box maxWidth="80%" mx="auto" my={4}>
			<PageTitle>Mobile Home Park Underwriting Calculator</PageTitle>
			<Grid container spacing={3} alignItems="stretch">
				{/* Left Side: Inputs */}
				<Grid item xs={12} md={4}>
					<Paper
						elevation={3}
						sx={{ p: 4, borderRadius: 4, mb: 4, height: '80%', display: 'flex', flexDirection: 'column' }}
					>
						<Typography variant="h5" fontWeight={700} mb={3} color="text.primary">
							Property Details
						</Typography>
						<Grid container spacing={3} sx={{ flex: 1 }}>
							<Grid item xs={12} sm={12}>
								<SliderWithInput
									value={inputs.numberOfLots}
									min={5}
									max={50}
									step={1}
									marks={numberOfLotsMarks}
									onChange={(value) => handleSliderChange('numberOfLots')({} as Event, value)}
									label="Number of Lots"
								/>
							</Grid>
							<Grid item xs={12} sm={12}>
								<SliderWithInput
									value={inputs.averageLotRent}
									min={500}
									max={2000}
									step={50}
									marks={averageLotRentMarks}
									onChange={(value) => handleSliderChange('averageLotRent')({} as Event, value)}
									label="Average Rent"
									formatValue={formatCurrency}
								/>
							</Grid>
							<Grid item xs={12} sm={12}>
								<SliderWithInput
									value={inputs.expenseRatio}
									min={0.1}
									max={0.6}
									step={0.01}
									marks={expenseRatioMarks}
									onChange={(value) => handleSliderChange('expenseRatio')({} as Event, value)}
									label="Expense Ratio"
									formatValue={formatPercentage}
								/>
							</Grid>
						</Grid>
					</Paper>
				</Grid>
				<Grid item xs={12} md={4}>
					<Paper
						elevation={3}
						sx={{ p: 4, borderRadius: 4, mb: 4, height: '80%', display: 'flex', flexDirection: 'column' }}
					>
						<Typography variant="h5" fontWeight={700} mb={3} color="text.primary">
							Loan Details
						</Typography>
						<Grid container spacing={3} sx={{ flex: 1 }}>
							<Grid item xs={12}>
								<SliderWithInput
									value={inputs.downPaymentPercentage}
									min={0.1}
									max={0.9}
									step={0.01}
									marks={downPaymentMarks}
									onChange={(value) =>
										handleSliderChange('downPaymentPercentage')({} as Event, value)
									}
									label="Down Payment"
									formatValue={formatPercentage}
								/>
							</Grid>
							<Grid item xs={12}>
								<SliderWithInput
									value={inputs.interestRate}
									min={0.03}
									max={0.12}
									step={0.001}
									marks={interestRateMarks}
									onChange={(value) => handleSliderChange('interestRate')({} as Event, value)}
									label="Interest Rate"
									formatValue={formatPercentage}
								/>
							</Grid>
							<Grid item xs={12}>
								<SliderWithInput
									value={inputs.loanTermYears}
									min={5}
									max={40}
									step={1}
									marks={loanTermMarks}
									onChange={(value) => handleSliderChange('loanTermYears')({} as Event, value)}
									label="Loan Term"
									formatValue={(value) => `${value} years`}
								/>
							</Grid>
						</Grid>
						{error && (
							<Fade in={!!error}>
								<Box mt={3} p={2} bgcolor="#fee2e2" color="#b91c1c" borderRadius={2}>
									{error}
								</Box>
							</Fade>
						)}
					</Paper>
				</Grid>
				{/* Right Side: Sliders */}
				<Grid item xs={12} md={4}>
					<Paper
						elevation={3}
						sx={{ p: 4, borderRadius: 4, mb: 4, height: '80%', display: 'flex', flexDirection: 'column' }}
					>
						<Typography variant="h5" fontWeight={700} mb={3} color="text.primary">
							Offer Price
						</Typography>
						<Box
							sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}
						>
							<Box sx={{ mb: 4 }}>
								<SliderWithInput
									value={inputs.offerPrice}
									min={minOfferPrice}
									max={maxOfferPrice}
									step={stepOfferPrice}
									marks={offerPriceMarks}
									onChange={(value) => handleSliderChange('offerPrice')({} as Event, value)}
									label="Offer Price"
									formatValue={formatCurrency}
								/>
							</Box>
							<Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', gap: 4 }}>
								<VerticalMetricBar value={results?.capRate || 0} label="Cap Rate" maxValue={0.15} />
								<VerticalMetricBar value={results?.roi || 0} label="ROI" maxValue={0.15} />
							</Box>
						</Box>
					</Paper>
				</Grid>
			</Grid>
			{results && (
				<Fade in={!!results}>
					<Box>
						<Paper elevation={2} sx={{ p: 3, borderRadius: 3, mb: 3 }}>
							<Typography variant="h5" fontWeight={700} mb={2} color="text.primary">
								Investment Analysis
							</Typography>
							<Grid container spacing={3}>
								<Grid item xs={12} sm={6} md={4}>
									<Typography variant="h6" fontWeight={600} mb={2} color="primary">
										Monthly Metrics
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">
										Monthly Income
									</Typography>
									<Typography variant="h6" fontWeight={600} mb={2}>
										{formatCurrency(results.monthlyIncome)}
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">
										Monthly Mortgage Payment
									</Typography>
									<Typography variant="h6" fontWeight={600} mb={2}>
										{formatCurrency(results.monthlyMortgagePayment)}
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">
										Monthly Cash Flow
									</Typography>
									<Typography variant="h6" fontWeight={600} mb={2}>
										{formatCurrency(results.monthlyCashFlow)}
									</Typography>
								</Grid>
								<Grid item xs={12} sm={6} md={4}>
									<Typography variant="h6" fontWeight={600} mb={2} color="primary">
										Annual Metrics
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">
										Annual Income
									</Typography>
									<Typography variant="h6" fontWeight={600} mb={2}>
										{formatCurrency(results.annualIncome)}
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">
										Annual Expenses
									</Typography>
									<Typography variant="h6" fontWeight={600} mb={2}>
										{formatCurrency(results.annualExpenses)}
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">
										Annual Cash Flow
									</Typography>
									<Typography variant="h6" fontWeight={600} mb={2}>
										{formatCurrency(results.annualCashFlow)}
									</Typography>
								</Grid>
								<Grid item xs={12} sm={6} md={4}>
									<Typography variant="h6" fontWeight={600} mb={2} color="primary">
										Loan Information
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">
										Down Payment
									</Typography>
									<Typography variant="h6" fontWeight={600} mb={2}>
										{formatCurrency(results.downPayment)}
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">
										Loan Amount
									</Typography>
									<Typography variant="h6" fontWeight={600} mb={2}>
										{formatCurrency(results.loanAmount)}
									</Typography>
									<Typography variant="subtitle2" color="text.secondary">
										NOI
									</Typography>
									<Typography variant="h6" fontWeight={600} mb={2}>
										{formatCurrency(results.noi)}
									</Typography>
								</Grid>
							</Grid>
						</Paper>
					</Box>
				</Fade>
			)}
		</Box>
	)
}

export default MobileHomeParkCalculator
